import { campoFalta } from '../hooks/useCamposObligatorios';
import type { EstadoGuia, Guia, OpcionPregunta, PasoCampo, PasoGuia, PasoPregunta, Texto } from './tipos';

/**
 * Reglas puras del motor de la ayuda interactiva: qué paso toca, si un campo está completo y los textos.
 * El recorrido y el dibujo están en GuiaInteractiva.tsx.
 */

type Campo = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
export const esCampo = (el: Element): el is Campo =>
  el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement;

/**
 * Elemento a resaltar: el primero visible que coincide con el selector (cada ítem de un remito se dibuja dos veces, fila
 * de escritorio y tarjeta de celular), o el primero si no hay ninguno visible (jsdom no mide).
 */
export function buscarObjetivo(selector: string, raiz: ParentNode = document): HTMLElement | null {
  const todos = Array.from(raiz.querySelectorAll<HTMLElement>(selector));
  return todos.find(el => el.getClientRects().length > 0) ?? todos[0] ?? null;
}

/** Un campo está completo si tiene valor y no le falta nada (obligatorio vacío o `data-falta`). Un botón, nunca. */
export function campoCompleto(el: HTMLElement): boolean {
  if (!esCampo(el)) return false;
  return !campoFalta(el) && !el.hasAttribute('data-falta') && el.value.trim() !== '';
}

export const textoDe = <C,>(texto: Texto<C>, estado: EstadoGuia<C>) => (typeof texto === 'function' ? texto(estado) : texto);

export const opcionesDe = <C,>(paso: PasoPregunta<C>, estado: EstadoGuia<C>): OpcionPregunta[] =>
  typeof paso.opciones === 'function' ? paso.opciones(estado) : paso.opciones;

export const pasoCompleto = <C,>(paso: PasoCampo<C>, estado: EstadoGuia<C>, el: HTMLElement) =>
  paso.completo ? paso.completo(estado, el) : campoCompleto(el);

/**
 * Si el paso corresponde ahora: se cumple su `cuando` y, si es una pregunta, tiene opciones; si es un campo, está en
 * pantalla. Lo usan `pasoPendiente` (al buscar el próximo paso) y la guía en curso (para dejar un paso que ya no va).
 */
export function pasoVigente<C>(paso: PasoGuia<C>, estado: EstadoGuia<C>, buscar = buscarObjetivo): boolean {
  if (paso.cuando && !paso.cuando(estado)) return false;
  if (paso.tipo === 'pregunta') return opcionesDe(paso, estado).length > 0;
  return !!buscar(paso.objetivo);
}

/** Si el paso se muestra: corresponde (`pasoVigente`) y, salvo `revisar` o una pregunta, falta completarlo. */
export function pasoPendiente<C>(paso: PasoGuia<C>, estado: EstadoGuia<C>, buscar = buscarObjetivo): boolean {
  if (!pasoVigente(paso, estado, buscar)) return false;
  if (paso.tipo === 'pregunta' || paso.revisar) return true;
  const el = buscar(paso.objetivo);
  return !!el && !pasoCompleto(paso, estado, el);
}

/** Índice del primer paso pendiente desde `desde` (inclusive); `guia.pasos.length` si no queda ninguno. */
export function resolverPaso<C>(guia: Guia<C>, desde: number, estado: EstadoGuia<C>, buscar = buscarObjetivo): number {
  for (let i = Math.max(0, desde); i < guia.pasos.length; i++) {
    if (pasoPendiente(guia.pasos[i], estado, buscar)) return i;
  }
  return guia.pasos.length;
}

export const indicePaso = <C,>(guia: Guia<C>, id: string) => guia.pasos.findIndex(p => p.id === id);
